// ============================================================
// SecaoAvaliacaoFisica.js — a tela da avaliação física
//
// Duas coisas em uma: o lançamento da coleta e a leitura da série.
//
// Elas ficam juntas porque é um fluxo só. Quem mede abre a tela do
// atleta, digita as dobras com o adipômetro na mão e vê o resultado
// aparecer ali. Separar em "cadastro" e "relatório" obrigaria a sair de
// uma tela e procurar a outra para descobrir que uma dobra ficou fora.
//
// Nenhuma conta mora aqui. Percentual de gordura, somatotipo, maturação
// e evolução saem de `avaliacaoFisica.js`, que é a mesma função que o
// Excel e o PDF vão usar. É a regra do painel inteiro e vale em dobro
// neste caso: equação de composição corporal errada dentro de um
// componente React só aparece quando alguém desconfia do número na tela.
//
// O que se grava são os números crus. O resultado é recalculado a cada
// leitura, então corrigir uma equação não exige reabrir documento nenhum.
// ============================================================

import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import {
  carregarMembrosGestao, carregarAvaliacoesFisicas, salvarAvaliacaoFisica,
} from "../firebase";
import {
  DOBRAS, PERIMETROS_POR_CAMPO, DIAMETROS, ALTURAS, PROTOCOLOS_DE_GORDURA,
  ESTAGIOS_MATURACIONAIS, compilarSerie, dobrasNecessarias,
  protocoloDaIdade, rotuloDoProtocolo, rotuloDoEstagio, adequacaoDoProtocolo,
  estagioSugerido, somatotipoMedio, coordenadasSomatocarta, maturacao,
  laudoDaMaturacao,
} from "../avaliacaoFisica";
import { idadeEm, idadeDecimalEm } from "../idade";
import { gerarPDFAvaliacaoFisica, gerarPDFFichaDeColeta } from "../pdfRelatorio";
import { useNomeNucleo } from "../NucleosContexto";
import {
  TEXTO, CINZA_ESC, BRANCO, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO,
  AZUL, OK, ATENCAO, RUIM,
} from "../tema";
import {
  Card, Btn, Sel, Input, Rotulo, ESTILO_CAMPO, StatusBox, Carregando, Vazio,
  Estatistica, FileiraDeEstatisticas, CampoDeBusca, GraficoDeMedida, INDICADORES,
  TrocaDeProtocolo,
  GraficoDeComposicao, Somatocarta,
} from "../componentes";
import useIsMobile from "../useIsMobile";

const HOJE = () => new Date().toISOString().slice(0, 10);

// Os grupos de medida, na ordem em que se mede no atleta: primeiro o que
// precisa de balança e estadiômetro, depois as dobras com o adipômetro,
// depois a fita, por último o paquímetro. Trocar de instrumento no meio
// da coleta é o que faz o avaliador perder a linha.
//
// A bateria é a inteira, e não só o que o protocolo do dia pede. O
// motivo está em `avaliacaoFisica.js`, no vocabulário das medidas, e
// resumido é este: as 76 coletas de 2026 ficaram sem os sítios de outro
// protocolo, e não existe voltar a medir uma dobra de março.
const GRUPOS = [
  {
    id: "basicas",
    titulo: "Peso e estatura",
    campos: [
      { id: "peso", rotulo: "Peso", unidade: "kg" },
      { id: "estatura", rotulo: "Estatura", unidade: "cm" },
    ],
  },
  { id: "dobras", titulo: "Dobras cutâneas", unidade: "mm", itens: DOBRAS },
  // Perímetro de membro tem os dois lados, e por isso a lista aqui é a
  // de CAMPOS e não a de sítios: braço (D) e braço (E) são dois campos.
  { id: "perimetros", titulo: "Perímetros", unidade: "cm", itens: PERIMETROS_POR_CAMPO },
  { id: "diametros", titulo: "Diâmetros ósseos", unidade: "cm", itens: DIAMETROS },
  { id: "alturas", titulo: "Alturas e comprimentos", unidade: "cm", itens: ALTURAS },
];

// Os campos que cada resultado precisa, além das dobras do protocolo. A
// tela marca com asterisco o que falta, e marcar só as dobras deixava o
// avaliador descobrir depois que o fracionamento não saiu por causa de um
// diâmetro.
const EXIGIDOS_PELO_FRACIONAMENTO = ["biestiloide", "femur"];
const EXIGIDOS_PELO_SOMATOTIPO = {
  perimetros: ["bracoRelaxado", "panturrilha"],
  diametros: ["umero", "femur"],
};
// Cintura e quadril não entram em equação de composição corporal, e sem
// elas não saem a razão cintura-quadril, a cintura-estatura nem o índice
// de conicidade, que são três dos quatro índices da tela.
const EXIGIDOS_PELOS_INDICES = ["cintura", "quadril"];
const BASICAS_EXIGIDAS = ["peso", "estatura"];

export default function SecaoAvaliacaoFisica({ userNucleo, podeEditar = true }) {
  const isMobile = useIsMobile();
  const nucleo = userNucleo || "";

  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [atletas, setAtletas] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [busca, setBusca] = useState("");
  const [modalidade, setModalidade] = useState("");
  const [selecionado, setSelecionado] = useState(null);

  const recarregar = useCallback(async () => {
    if (!nucleo) return;
    setCarregando(true);
    try {
      // `carregarMembrosGestao` e não `carregarAtletas`: esta tela precisa
      // do perfil, do sexo e da data de nascimento, e o outro carregador
      // devolve só o que a grade de presença usa — sem `perfil`, o filtro
      // abaixo nunca casava e a lista de atletas abria vazia; sem `sexo` e
      // `dataNascimento`, as equações não tinham resultado para ninguém.
      const [pessoas, coletas] = await Promise.all([
        carregarMembrosGestao(nucleo),
        carregarAvaliacoesFisicas(nucleo),
      ]);
      setAtletas(pessoas.filter(p => (p.perfil || "").toLowerCase().includes("atleta")));
      setAvaliacoes(coletas);
    } catch (e) {
      setStatus({ tipo: "erro", texto: `Não consegui carregar: ${e.message}` });
    } finally {
      setCarregando(false);
    }
  }, [nucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const modalidades = useMemo(
    () => [...new Set(atletas.map(a => a.modalidade).filter(Boolean))].sort(),
    [atletas],
  );

  // O inativo sai da lista, e as coletas dele ficam.
  //
  // São duas perguntas diferentes: "quem eu meço este mês" e "o que foi
  // medido". Quem saiu do programa não aparece para ser avaliado, e as
  // medidas que ele fez enquanto estava continuam valendo e continuam
  // entrando nas médias daquelas datas. Apagar o histórico de quem saiu
  // mudaria a média de janeiro toda vez que alguém se desligasse.
  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return atletas
      .filter(a => a.ativo !== false)
      .filter(a => !modalidade || a.modalidade === modalidade)
      .filter(a => !termo || (a.nome || "").toLowerCase().includes(termo))
      .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
  }, [atletas, busca, modalidade]);

  const porAtleta = useMemo(() => {
    const mapa = {};
    avaliacoes.forEach(av => {
      (mapa[av.atletaUid] = mapa[av.atletaUid] || []).push(av);
    });
    return mapa;
  }, [avaliacoes]);

  if (!nucleo) return <Vazio titulo="Selecione um núcleo." />;
  if (carregando) return <Carregando />;

  const atleta = selecionado ? atletas.find(a => a.uid === selecionado) : null;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      {status && <StatusBox tipo={status.tipo} msg={status.texto} />}

      {atleta ? (
        <FichaDoAtleta
          atleta={atleta}
          coletas={porAtleta[atleta.uid] || []}
          nucleo={nucleo}
          podeEditar={podeEditar}
          isMobile={isMobile}
          onVoltar={() => setSelecionado(null)}
          onSalvou={async () => {
            await recarregar();
            setStatus({ tipo: "ok", texto: "Avaliação gravada." });
          }}
          onErro={msg => setStatus({ tipo: "erro", texto: msg })}
        />
      ) : (
        <ListaDeAtletas
          lista={lista}
          porAtleta={porAtleta}
          modalidades={modalidades}
          modalidade={modalidade}
          setModalidade={setModalidade}
          busca={busca}
          setBusca={setBusca}
          isMobile={isMobile}
          onAbrir={setSelecionado}
          nucleo={nucleo}
          onErro={msg => setStatus({ tipo: "erro", texto: msg })}
        />
      )}
    </div>
  );
}

// ── Lista ────────────────────────────────────────────────────

function ListaDeAtletas({
  lista, porAtleta, modalidades, modalidade, setModalidade, busca, setBusca, isMobile,
  onAbrir, nucleo, onErro,
}) {
  const comColeta = lista.filter(a => (porAtleta[a.uid] || []).length > 0).length;
  const nomeDoNucleo = useNomeNucleo();
  const [gerandoFichas, setGerandoFichas] = useState(false);

  // As folhas do dia, numa tacada: uma página por atleta do recorte que
  // está na tela. É assim que a coleta acontece, uma turma de cada vez,
  // e imprimir atleta por atleta seria trinta cliques.
  async function baixarFichas() {
    setGerandoFichas(true);
    try {
      await gerarPDFFichaDeColeta({ atletas: lista, nucleo, nucleoNome: nomeDoNucleo(nucleo) });
    } catch (e) {
      onErro(`Não consegui gerar as fichas: ${e.message}`);
    } finally {
      setGerandoFichas(false);
    }
  }

  return (
    <Card>
      <FileiraDeEstatisticas>
        <Estatistica rotulo="Atletas" valor={lista.length} />
        <Estatistica rotulo="Com avaliação" valor={comColeta} />
        <Estatistica rotulo="Sem avaliação" valor={lista.length - comColeta} />
      </FileiraDeEstatisticas>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: `${ESP.lg}px 0` }}>
        <CampoDeBusca valor={busca} onChange={setBusca} placeholder="Buscar atleta" />
        <Sel value={modalidade} onChange={setModalidade}
          options={[{ value: "", label: "Todas as modalidades" },
            ...modalidades.map(m => ({ value: m, label: m }))]} />
        {lista.length > 0 && (
          <Btn variant="secondary" onClick={baixarFichas} disabled={gerandoFichas}>
            {gerandoFichas
              ? "Gerando…"
              : `Fichas de coleta em papel (${lista.length})`}
          </Btn>
        )}
      </div>

      {lista.length === 0 ? (
        <Vazio titulo="Nenhum atleta neste recorte." />
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {lista.map(a => {
            const coletas = porAtleta[a.uid] || [];
            const ultima = coletas[coletas.length - 1];
            return (
              <button key={a.uid} onClick={() => onAbrir(a.uid)} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, width: "100%", textAlign: "left", cursor: "pointer",
                background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
                padding: "11px 13px", font: "inherit",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: TEXTO, ...TIPO.corpo }}>
                    {a.nome}
                  </div>
                  <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                    {a.modalidade || "—"}
                    {a.dataNascimento && ` · ${idadeEm(a.dataNascimento)} anos`}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ ...TIPO.apoio, color: coletas.length ? TEXTO : CINZA_ESC }}>
                    {coletas.length
                      ? `${coletas.length} avaliaç${coletas.length > 1 ? "ões" : "ão"}`
                      : "sem avaliação"}
                  </div>
                  {ultima && (
                    <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                      última em {emBR(ultima.data)}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Ficha do atleta ──────────────────────────────────────────

function FichaDoAtleta({
  atleta, coletas, nucleo, podeEditar, isMobile, onVoltar, onSalvou, onErro,
}) {
  const nomeDoNucleo = useNomeNucleo();
  const [editando, setEditando] = useState(null);
  const [imprimindo, setImprimindo] = useState(false);
  // Qual coleta está aberta no detalhe. `null` é a última, que é o que
  // se quer ver em nove de dez vezes.
  const [coletaAberta, setColetaAberta] = useState(null);

  // A compilação é da série inteira, e não de uma coleta isolada, porque
  // é a série que escolhe o método de maturação e a convenção do braço
  // para todos os pontos de uma vez. Compilar ponto a ponto deixaria a
  // primeira coleta num método e a última em outro.
  const serie = useMemo(
    () => compilarSerie({
      sexo: atleta.sexo,
      dataNascimento: atleta.dataNascimento,
      avaliacoes: coletas,
    }),
    [atleta.sexo, atleta.dataNascimento, coletas],
  );

  const faltaSexo = serie.some(p => p.faltaSexo) || (!atleta.sexo && coletas.length > 0);
  const faltaNascimento = !atleta.dataNascimento;

  const ponto = serie.find(p => p.data === coletaAberta) || serie[serie.length - 1] || null;

  // Os pontos da somatocarta e o ponto médio da série.
  //
  // As coordenadas vêm do módulo e não são recalculadas aqui: a mesma
  // projeção tem de valer na tela, no PDF e no Excel.
  const pontosDaCarta = useMemo(
    () => serie
      .filter(p => p.somatotipo && p.somatotipo.completo)
      .map(p => ({
        data: p.data,
        endomorfia: p.somatotipo.endomorfia,
        mesomorfia: p.somatotipo.mesomorfia,
        ectomorfia: p.somatotipo.ectomorfia,
        classificacao: p.somatotipo.classificacao,
        ...coordenadasSomatocarta(
          p.somatotipo.endomorfia, p.somatotipo.mesomorfia, p.somatotipo.ectomorfia,
        ),
      })),
    [serie],
  );
  // A folha impressa sai da MESMA série que a tela desenha, e leva junto a
  // síntese da coleta aberta. Recompilar aqui criaria um segundo lugar
  // onde o percentual de gordura é calculado, e no dia em que um protocolo
  // mudasse, papel e tela passariam a discordar sem ninguém perceber.
  async function imprimir() {
    setImprimindo(true);
    try {
      const daColeta = ponto ? coletas.find(c => c.data === ponto.data) : null;
      await gerarPDFAvaliacaoFisica({
        atleta,
        serie,
        nucleo,
        nucleoNome: nomeDoNucleo(nucleo),
        sintese: (daColeta && daColeta.sintese) || null,
      });
    } catch (e) {
      onErro(`Não consegui gerar o PDF: ${e.message}`);
    } finally {
      setImprimindo(false);
    }
  }

  // A folha EM BRANCO, para medir sem tela.
  //
  // É a outra metade da impressão: a de cima leva a avaliação pronta
  // para a pasta, esta vai para a pista quando falta internet ou o
  // aparelho não está à mão. Os campos saem das mesmas listas que o
  // formulário usa, então papel e tela pedem a mesma coisa.
  const [gerandoFicha, setGerandoFicha] = useState(false);
  async function baixarFichaDeColeta() {
    setGerandoFicha(true);
    try {
      await gerarPDFFichaDeColeta({
        atletas: [atleta], nucleo, nucleoNome: nomeDoNucleo(nucleo),
      });
    } catch (e) {
      onErro(`Não consegui gerar a ficha: ${e.message}`);
    } finally {
      setGerandoFicha(false);
    }
  }

  const medioDaCarta = useMemo(
    () => somatotipoMedio(serie.map(p => p.somatotipo)),
    [serie],
  );

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      <Card>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap",
        }}>
          <div>
            <Btn variant="discreto" tamanho="sm" onClick={onVoltar}>← Todos os atletas</Btn>
            <h2 style={{ margin: "6px 0 0", ...TIPO.titulo, color: TEXTO }}>
              {atleta.nome}
            </h2>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
              {atleta.modalidade || "—"}
              {atleta.dataNascimento && ` · ${idadeEm(atleta.dataNascimento)} anos`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {/* A folha impressa é o que vai para a pasta do atleta e para a
                sala de treino, onde não há tela. Sem coleta nenhuma não há
                o que imprimir, e o botão some em vez de gerar folha vazia. */}
            {serie.length > 0 && (
              <Btn variant="secondary" onClick={imprimir} disabled={imprimindo}>
                {imprimindo ? "Gerando…" : "Imprimir em PDF"}
              </Btn>
            )}
            {podeEditar && (
              <Btn variant="secondary" onClick={baixarFichaDeColeta} disabled={gerandoFicha}>
                {gerandoFicha ? "Gerando…" : "Ficha de coleta em papel"}
              </Btn>
            )}
            {podeEditar && (
              <Btn onClick={() => setEditando({ data: HOJE(), dobras: {}, perimetros: {}, diametros: {} })}>
                Nova avaliação
              </Btn>
            )}
          </div>
        </div>

        {/* O cadastro incompleto trava a conta inteira, e a tela precisa
            dizer qual campo, não "não foi possível calcular". Sexo e data
            de nascimento entram em todas as equações. */}
        {(faltaSexo || faltaNascimento) && (
          <div style={{ marginTop: ESP.lg }}>
            <StatusBox tipo="aviso" msg={
              `Falta ${[faltaSexo && "o sexo", faltaNascimento && "a data de nascimento"]
                .filter(Boolean).join(" e ")} no cadastro deste atleta. As equações de ` +
              "composição corporal e de maturação são específicas por sexo e por idade, " +
              "e sem esses campos elas não têm resultado."
            } />
          </div>
        )}
      </Card>

      {editando && podeEditar && (
        <FormularioDeColeta
          valores={editando}
          atleta={atleta}
          serie={serie}
          nucleo={nucleo}
          isMobile={isMobile}
          onCancelar={() => setEditando(null)}
          onSalvou={async () => { setEditando(null); await onSalvou(); }}
          onErro={onErro}
        />
      )}

      {serie.length === 0 ? (
        <Card><Vazio titulo="Este atleta ainda não tem avaliação física." /></Card>
      ) : (
        <>
          <Card titulo="Resultados por avaliação">
            <TabelaDaSerie serie={serie} isMobile={isMobile}
              onEditar={podeEditar ? p => setEditando(coletas.find(c => c.data === p.data)) : null} />
            <ComoLer />
          </Card>

          {/* As medidas cruas em quadro próprio. Os resultados acima são
              conta; isto é o que o avaliador anotou, e é o que se confere
              quando um resultado parece estranho. */}
          <Card titulo="Medidas coletadas">
            <TabelaDeMedidas coletas={coletas} />
          </Card>
          {ponto && (
            <Card titulo="Composição corporal e índices">
              <div style={{ display: "grid", gap: ESP.lg }}>
                {/* A coleta aberta se escolhe, e o padrão é a última.
                    Sem o seletor, conferir a coleta de março obrigaria a
                    sair da tela e voltar. */}
                {serie.length > 1 && (
                  <div style={{ maxWidth: 260 }}>
                    <Sel
                      label="Coleta"
                      value={ponto.data}
                      onChange={setColetaAberta}
                      options={[...serie].reverse().map(p => ({
                        value: p.data,
                        label: `${emBR(p.data)}${p.idade != null ? ` · ${dec(p.idade)} anos` : ""}`,
                      }))}
                    />
                  </div>
                )}
                <DetalheDaColeta ponto={ponto} isMobile={isMobile} />
              </div>
            </Card>
          )}

          {/* Um gráfico por medida, pequeno, com a unidade no título e o
              valor escrito no ponto.

              Eram dois gráficos de duas linhas com eixo de cada lado, e
              o Diego apontou o problema: com escalas diferentes, a
              proximidade entre as curvas é produzida pela escala e não
              pelos dados, e a magnitude de cada mudança fica ilegível.
              Lado a lado, estes comparam datas sem sugerir relação entre
              medidas. */}
          <Card titulo="Evolução">
            <div style={{
              display: "grid", gap: ESP.xl,
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
            }}>
              {[
                INDICADORES.peso,
                INDICADORES.estatura,
                INDICADORES.imc,
                INDICADORES.gordura,
                INDICADORES.massaAdiposa,
                INDICADORES.massaMuscular,
              ].map(ind => (
                <GraficoDeMedida key={ind.id} serie={serie} indicador={ind} />
              ))}
            </div>
            {/* A série de gordura atravessou a virada dos 18 anos.
                O protocolo muda sozinho ali, e é certo que mude: Deurenberg
                para de valer. Mas a curva do gráfico não sabe disso, e uma
                parte do degrau entre o último ponto de criança e o primeiro
                de adulto é troca de equação, não mudança do atleta. Quem lê
                o gráfico precisa saber antes de concluir alguma coisa. */}
            <TrocaDeProtocolo serie={serie} />
            <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: ESP.md }}>
              Cada medida tem o seu desenho e a sua escala. Duas medidas no mesmo gráfico,
              com um eixo de cada lado, fariam as curvas se aproximarem ou se cruzarem por
              causa da escala escolhida, e não por causa do atleta.
            </div>
          </Card>

          <Card titulo="Somatocarta">
            <Somatocarta pontos={pontosDaCarta} medio={medioDaCarta} />
          </Card>
          <Card titulo="O que mudou">
            <Evolucao serie={serie} />
          </Card>

          {/* A síntese fecha a tela porque é o que se faz com tudo que
              está acima: ela é a ponte entre avaliar e treinar, e vem
              depois de o leitor ter visto os números. */}
          {ponto && (
            <Card titulo={`Síntese da equipe · avaliação de ${emBR(ponto.data)}`}>
              <SinteseDaEquipe
                ponto={ponto}
                coleta={coletas.find(c => c.data === ponto.data) || null}
                atleta={atleta}
                nucleo={nucleo}
                podeEditar={podeEditar}
                onSalvou={onSalvou}
                onErro={onErro}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── De onde vem cada classificação ───────────────────────────

// A tabela de resultados usa três rótulos que não se explicam sozinhos:
// "Adequado" na gordura, "Circa-PVC" na maturação e o aviso de intervalo
// curto na velocidade de crescimento. Os três vêm de referências
// publicadas, e nenhum deles é medida: são leituras feitas em cima dela.
//
// A explicação fica aberta por um clique, e não em rodapé nem em balão
// de passar o mouse. Balão não existe no celular e some ao sair, rodapé
// ninguém lê, e esta tela é mostrada para atleta e para família.
//
// O conteúdo é curto de propósito. Quem quiser a equação inteira tem o
// módulo de cálculo; aqui vai o que muda a leitura do número.
const EXPLICACOES = [
  {
    rotulo: "Gordura: muito baixo, baixo, adequado, alto",
    texto:
      "As faixas são de Lohman (1987), específicas por sexo e pensadas para criança e "
      + "adolescente. Elas são referência de SAÚDE, e não de desempenho: atleta de "
      + "rendimento costuma ficar abaixo de \"adequado\" sem que isso seja achado clínico. "
      + "O percentual em si é estimativa feita a partir das dobras, não medida direta, e "
      + "depende do protocolo e do estágio maturacional que a coleta registrou.",
  },
  {
    rotulo: "Maturação: Pré-PVC, Circa-PVC, Pós-PVC",
    texto:
      "PVC é o pico de velocidade de crescimento, o momento em que o atleta cresce mais "
      + "rápido na vida. Pré-PVC é faltar mais de um ano para ele, Circa-PVC é estar na "
      + "janela de um ano para cada lado, e Pós-PVC é já ter passado há mais de um ano. "
      // A frase sobre dois atletas da mesma idade em fases diferentes, e
      // sobre teste físico nessa fase medir também o crescimento, saiu
      // em 16/10/2026: o Diego mandou tirá-la da ressalva da coleta, e
      // ela estava repetida aqui quase com as mesmas palavras.
      + "É ESTIMATIVA, calculada por Moore (2015) ou Mirwald (2002) a partir de idade, "
      + "estatura e peso, com erro de cerca de meio ano para mais ou para menos.",
  },
  {
    rotulo: "Intervalo curto",
    texto:
      "Aparece quando duas coletas estão a menos de seis meses uma da outra. A velocidade "
      + "de crescimento em centímetros por ano divide o que cresceu pelo tempo entre as "
      + "duas, e em intervalo curto o erro da fita, meio centímetro, vira vários "
      + "centímetros por ano. O cálculo é apresentado com o aviso, porque o número "
      + "existe, mas não é adequado a essa leitura.",
  },
  {
    rotulo: "As quatro massas",
    texto:
      "A massa adiposa sai do protocolo de dobras. A óssea sai da equação de Von Döbeln "
      + "adaptada por Rocha (1975), que usa estatura, diâmetro do punho e do fêmur, e que foi "
      + "derivada em brasileiros de 17 a 25 anos: abaixo dessa idade o valor serve para "
      + "acompanhar a evolução do próprio atleta, não para comparar com referência. A "
      + "residual é proporção fixa do peso, 24,1% no homem e 20,9% na mulher, por Würch "
      + "(1974). A muscular é o que sobra, peso menos as outras três, e por isso acumula o "
      + "erro delas: um percentual de gordura dois pontos acima resulta em dois pontos "
      + "de massa muscular a menos, sem que o número indique a origem do desvio.",
  },
  {
    rotulo: "Protocolo de gordura",
    texto:
      "Até os 17 anos vale Deurenberg (4 dobras); dos 18 em diante, Jackson e Pollock ou "
      + "Guedes (3 dobras), à escolha de quem mede. A idade na data da coleta decide, e "
      + "escolha que não serve para a idade fica gravada sem efeito, com aviso na linha. "
      + "Protocolos diferentes não se comparam entre si: a série de um atleta só tem "
      + "sentido quando todos os pontos saíram do mesmo.",
  },
];

function ComoLer() {
  return (
    <details style={{ marginTop: ESP.md }}>
      <summary style={{
        cursor: "pointer", ...TIPO.apoio, color: AZUL, fontWeight: 600,
      }}>
        De onde vêm estas classificações
      </summary>
      <div style={{ display: "grid", gap: ESP.md, marginTop: ESP.md }}>
        {EXPLICACOES.map(e => (
          <div key={e.rotulo}>
            <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>{e.rotulo}</div>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{e.texto}</div>
          </div>
        ))}
      </div>
    </details>
  );
}

// ── Tabela da série ──────────────────────────────────────────

function TabelaDaSerie({ serie, isMobile, onEditar }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", ...TIPO.apoio }}>
        <thead>
          <tr style={{ background: FUNDO_SUAVE }}>
            <Th>Avaliação</Th>
            <Th>Idade</Th>
            <Th>Peso</Th>
            <Th>Estatura</Th>
            <Th>IMC</Th>
            <Th>% gordura</Th>
            <Th>Massa magra</Th>
            <Th>Maturação</Th>
            {onEditar && <Th />}
          </tr>
        </thead>
        <tbody>
          {serie.map(p => (
            <tr key={p.data} style={{ borderTop: `1px solid ${BORDA}` }}>
              <Td forte>{emBR(p.data)}</Td>
              <Td>{dec(p.idade)}</Td>
              <Td>{num(p.peso, "kg")}</Td>
              <Td>{num(p.estatura, "cm")}</Td>
              {/* IMC e massa magra com UMA casa, como no detalhe da
                  coleta e como no relatório. `num` mostra o número como
                  ele foi calculado, e os dois saem com duas casas do
                  domínio: a mesma coleta aparecia com 19,16 nesta tabela
                  e 19,2 logo abaixo, na mesma tela. */}
              <Td>{dec(p.indices.imc)}</Td>
              <Td><Gordura d={p.deurenberg} ignorado={p.protocoloIgnorado} /></Td>
              <Td>{p.deurenberg?.massaMagra != null ? `${dec(p.deurenberg.massaMagra)} kg` : "—"}</Td>
              <Td><Maturacao m={p.maturacao} /></Td>
              {onEditar && (
                <Td>
                  <Btn variant="discreto" tamanho="sm" onClick={() => onEditar(p)}>editar</Btn>
                </Td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// O percentual de gordura, com a faixa de referência ao lado.
//
// A faixa é de SAÚDE e não de desempenho, e o rótulo precisa dizer isso
// perto do número: atleta de rendimento fica abaixo de "adequado" sem
// que isso seja achado clínico, e um "Muito baixo" em vermelho ao lado
// do nome de uma criança assusta quem lê sem contexto.
function Gordura({ d, ignorado }) {
  if (!d) return <span style={{ color: CINZA_ESC }}>—</span>;
  if (!d.completo) return <Falta itens={d.faltam} />;
  return (
    <span title={`protocolo de ${rotuloDoProtocolo(d.protocolo) || "—"}`}>
      <strong>{dec(d.percentualGordura)}%</strong>
      <span style={{ color: CINZA_ESC, marginLeft: 6 }}>{d.classificacao}</span>
      {/* A coleta guardou uma escolha de protocolo que não vale para a
          idade daquele dia. O número ao lado saiu pelo protocolo da
          idade, e a linha tem que dizer isso: senão o relatório parece
          estar respondendo à escolha gravada. */}
      {ignorado && (
        <div style={{ fontSize: 11, color: ATENCAO }}>
          protocolo escolhido não valia nesta idade; valeu o da idade
        </div>
      )}
    </span>
  );
}


// O estágio vem com o pico estimado e com a margem de erro, sempre.
//
// "Pico aos 12,2" lido sozinho vira data de nascimento na cabeça de quem
// lê. O método tem meio ano de erro padrão, e o relatório que esconde
// isso faz a comissão técnica planejar carga em cima de uma precisão que
// não existe.
function Maturacao({ m }) {
  if (!m || !m.completo) return <span style={{ color: CINZA_ESC }}>—</span>;
  return (
    <span title={`método de ${m.metodo === "mirwald" ? "Mirwald" : "Moore"}`}>
      <strong>{m.estagio}</strong>
      {/* "estimado" na célula, e não só na legenda: o estágio tem cara
          de medida e não é. Sai de uma equação que acerta o pico com
          erro de meio ano para mais ou para menos, e quem lê a tabela
          corrida precisa ver isso no mesmo lugar que vê o número. */}
      <span style={{ color: CINZA_ESC, marginLeft: 6 }}>
        pico estimado aos {dec(m.idadeNoPico)} ± {dec(m.erroPadrao, 2)} anos
      </span>
    </span>
  );
}

// O que falta medir, por nome.
//
// Não é "incompleto": é a lista do que buscar. Quem está com o
// adipômetro na mão precisa saber qual dobra abrir, e "faltam 2 dobras"
// obriga a pessoa a ir conferir qual, numa tela que ela não tem aberta.
function Falta({ itens }) {
  const nomes = (itens || []).map(rotuloDoCampo).join(", ");
  return (
    <span style={{ color: ATENCAO, ...TIPO.apoio }} title={nomes}>
      falta {nomes || "medida"}
    </span>
  );
}

// ── Evolução ─────────────────────────────────────────────────

function Evolucao({ serie }) {
  const comDelta = serie.filter(p => p.delta);
  if (!comDelta.length) {
    return <Vazio titulo="A comparação aparece a partir da segunda avaliação." />;
  }
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {comDelta.map((p, i) => {
        const d = p.delta;
        // Na segunda avaliação, "desde a anterior" e "desde a primeira"
        // são a mesma conta, e repetir os cinco números confundiria em
        // vez de informar.
        const a = i > 0 ? p.desdeAPrimeira : null;
        return (
          <div key={p.data} style={{
            border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "10px 13px",
          }}>
            <div style={{ fontWeight: 600, color: TEXTO, marginBottom: 6 }}>
              {emBR(p.data)}
              <span style={{ fontWeight: 400, color: CINZA_ESC, marginLeft: 8 }}>
                {d.anos != null && `${(d.anos * 12).toFixed(0)} meses depois da anterior`}
              </span>
            </div>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", ...TIPO.apoio }}>
              <Variacao rotulo="Estatura" valor={d.estatura} unidade="cm" bomSeSobe />
              <Variacao rotulo="Peso" valor={d.peso} unidade="kg" neutro />
              <Variacao rotulo="Massa magra" valor={d.massaMagra} unidade="kg" bomSeSobe />
              <Variacao rotulo="Gordura" valor={d.percentualGordura} unidade="pp" bomSeDesce />
              {d.somatotipo != null && (
                <Variacao rotulo="Mudança de forma" valor={d.somatotipo} neutro />
              )}
            </div>

            {/* O acumulado desde a primeira avaliação, embaixo do
                intervalo. Não aparece na segunda coleta, onde os dois
                números seriam o mesmo.

                Ele existe porque a soma dos intervalos some da vista:
                três quedas de meio ponto de gordura seguidas são um ponto
                e meio, e nenhuma das três parecia muito. */}
            {a !== null && (
              <div style={{
                marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${BORDA}`,
              }}>
                <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginBottom: 4 }}>
                  Desde a primeira avaliação, em {emBR(p.dataDaPrimeira)}
                  {a.anos != null && `, ${(a.anos * 12).toFixed(0)} meses`}
                </div>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", ...TIPO.apoio }}>
                  <Variacao rotulo="Estatura" valor={a.estatura} unidade="cm" bomSeSobe />
                  <Variacao rotulo="Peso" valor={a.peso} unidade="kg" neutro />
                  <Variacao rotulo="Massa magra" valor={a.massaMagra} unidade="kg" bomSeSobe />
                  <Variacao rotulo="Gordura" valor={a.percentualGordura} unidade="pp" bomSeDesce />
                  {a.somatotipo != null && (
                    <Variacao rotulo="Mudança de forma" valor={a.somatotipo} neutro />
                  )}
                </div>
              </div>
            )}

            {/* Os dois avisos que impedem o relatório de publicar número
                que não existe. Ficam ao lado do próprio valor, e não num
                rodapé que ninguém lê. */}
            {d.estaturaRegrediu && (
              <div style={{ marginTop: 6, ...TIPO.apoio, color: RUIM }}>
                A estatura diminuiu em relação à avaliação anterior, o que não acontece
                nesta idade. Uma das duas medidas está errada.
              </div>
            )}
            {!d.estaturaRegrediu && d.intervaloCurto && d.velocidadeDeCrescimento != null && (
              <div style={{ marginTop: 6, ...TIPO.apoio, color: ATENCAO }}>
                Intervalo curto demais para estimar velocidade de crescimento: o cálculo
                resultaria em {String(d.velocidadeDeCrescimento).replace(".", ",")} cm/ano, que é o erro
                da fita dividido por um intervalo pequeno.
              </div>
            )}
            {!d.intervaloCurto && d.velocidadeDeCrescimento != null && (
              <div style={{ marginTop: 6, ...TIPO.apoio, color: CINZA_ESC }}>
                Velocidade de crescimento: {String(d.velocidadeDeCrescimento).replace(".", ",")} cm/ano.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Variacao({ rotulo, valor, unidade = "", bomSeSobe, bomSeDesce, neutro }) {
  if (valor == null) return null;
  const cor = neutro ? TEXTO
    : valor === 0 ? CINZA_ESC
    : (valor > 0) === Boolean(bomSeSobe) && !bomSeDesce ? OK
    : (valor < 0) === Boolean(bomSeDesce) && !bomSeSobe ? OK
    : RUIM;
  const sinal = valor > 0 ? "+" : "";
  return (
    <span>
      <span style={{ color: CINZA_ESC }}>{rotulo} </span>
      <strong style={{ color: cor }}>
        {sinal}{String(valor).replace(".", ",")}{unidade && ` ${unidade}`}
      </strong>
    </span>
  );
}

// ── Formulário ───────────────────────────────────────────────

function FormularioDeColeta({
  valores, atleta, serie = [], nucleo, isMobile, onCancelar, onSalvou, onErro,
}) {
  const [form, setForm] = useState(() => ({
    data: valores.data || HOJE(),
    // "" é o automático: a idade na data da coleta decide. Guardar a
    // escolha vazia, e não o protocolo que ela resolve hoje, é o que faz
    // a coleta continuar certa se a data mudar no meio do lançamento.
    protocoloGordura: valores.protocoloGordura || "",
    estagioMaturacional: valores.estagioMaturacional || valores.maturacao || "",
    peso: valores.peso ?? "",
    estatura: valores.estatura ?? "",
    dobras: { ...(valores.dobras || {}) },
    perimetros: { ...(valores.perimetros || {}) },
    diametros: { ...(valores.diametros || {}) },
    // As alturas são campos de topo no documento, como a estatura, e
    // viram grupo só na tela: `estaturaSentado` já estava gravado assim
    // em 2026 e é o que a equação de Mirwald lê.
    alturas: {
      estaturaSentado: valores.estaturaSentado ?? "",
      envergadura: valores.envergadura ?? "",
      alturaTrocanterica: valores.alturaTrocanterica ?? "",
    },
  }));
  const [salvando, setSalvando] = useState(false);
  // Vira verdadeiro quando o avaliador tentou gravar com campo faltando
  // e a tela recusou. O segundo toque grava.
  const [insistindo, setInsistindo] = useState(false);

  const mudar = (grupo, campo, v) =>
    setForm(f => (grupo
      ? { ...f, [grupo]: { ...f[grupo], [campo]: v } }
      : { ...f, [campo]: v }));

  // A idade NA DATA DA COLETA, e não hoje.
  //
  // Importa no aniversário de 18 anos: uma reavaliação lançada depois,
  // referente a uma coleta de antes, tem que pedir as dobras que valiam
  // naquele dia. Usar a idade de hoje mudaria o protocolo de uma medida
  // que já aconteceu.
  const idadeNaColeta = idadeDecimalEm(atleta.dataNascimento, form.data);

  // O automático, e o que está valendo.
  //
  // São dois valores porque a tela precisa dizer os dois: qual protocolo
  // a idade manda e qual o avaliador pôs no lugar. Mostrar só o segundo
  // esconderia a troca de quem vai ler a série depois.
  const automatico = protocoloDaIdade(idadeNaColeta);
  const protocolo = form.protocoloGordura || automatico;
  const adequacao = protocolo
    ? adequacaoDoProtocolo({ protocolo, sexo: atleta.sexo, idade: idadeNaColeta })
    : null;

  // O estágio maturacional sugerido, pela mesma conta que a compilação
  // usa: o pico de velocidade de crescimento quando dá para estimar, e a
  // idade quando não dá. Calcular aqui de outro jeito faria a tela
  // prometer um estágio e o resultado sair por outro.
  const mat = maturacao({
    sexo: atleta.sexo,
    idade: idadeNaColeta,
    estatura: form.estatura,
    estaturaSentado: form.alturas.estaturaSentado,
    peso: form.peso,
  });
  const estagioAutomatico = estagioSugerido({
    sexo: atleta.sexo,
    idade: idadeNaColeta,
    offsetPVC: mat && mat.completo ? mat.offset : null,
  });
  const estagio = form.estagioMaturacional || estagioAutomatico;

  // As dobras seguem o protocolo ESCOLHIDO, e não o da idade. Quem
  // escolheu Guedes tem que ver os sítios de Guedes na tela, mesmo que a
  // escolha vá acabar recusada: é o que deixa o avaliador conferir a
  // escolha olhando os campos, antes de medir.
  const necessarias = dobrasNecessarias(protocolo, atleta.sexo);

  // Protocolo diferente do que as outras coletas deste atleta usaram.
  //
  // Só vale aviso quando a troca é escolha: na virada dos 18 anos o
  // protocolo muda sozinho e avisar ali seria barulho. O que não pode
  // passar em silêncio é alguém pôr Guedes numa série medida em Pollock,
  // porque as duas equações respondem a dobras diferentes e a diferença
  // entre elas apareceria no gráfico como se o atleta tivesse mudado.
  const anteriores = [...new Set(
    serie
      .filter(p => p.data !== form.data)
      .map(p => p.deurenberg && p.deurenberg.protocolo)
      .filter(Boolean),
  )];
  const mistura = Boolean(form.protocoloGordura)
    && anteriores.length > 0
    && !anteriores.includes(protocolo);

  // O que marcar com asterisco em cada grupo. Marcar só as dobras
  // deixava o avaliador descobrir depois que o fracionamento não saiu
  // por causa de um diâmetro que ninguém pediu na tela.
  function exigidosDoGrupo(grupo) {
    if (grupo === "basicas") return BASICAS_EXIGIDAS;
    if (grupo === "dobras") return necessarias;
    if (grupo === "perimetros") {
      return [...new Set([
        ...EXIGIDOS_PELO_SOMATOTIPO.perimetros,
        ...EXIGIDOS_PELOS_INDICES,
      ])];
    }
    if (grupo === "diametros") {
      return [...new Set([
        ...EXIGIDOS_PELO_SOMATOTIPO.diametros,
        ...EXIGIDOS_PELO_FRACIONAMENTO,
      ])];
    }
    return [];
  }

  // O que falta para a coleta ficar completa, pelo nome, na ordem em que
  // os campos aparecem na tela.
  //
  // É a MESMA lista que marca o campo com asterisco: o que a tela pede
  // enquanto se mede é o que ela cobra na hora de gravar, senão o
  // avaliador descobre a falta depois que o atleta foi embora.
  const pendencias = GRUPOS.flatMap(g => {
    const exigidos = exigidosDoGrupo(g.id);
    if (!exigidos.length) return [];
    const itens = g.campos || g.itens;
    return itens
      .filter(c => exigidos.includes(c.id) && !String(
        (g.campos ? form[c.id] : form[g.id][c.id]) ?? "",
      ).trim())
      .map(c => (g.campos ? c.rotulo : `${c.rotulo} (${g.titulo.toLowerCase()})`));
  });

  async function salvar() {
    if (!form.data) return onErro("A avaliação precisa de uma data.");
    // Coleta incompleta não grava na primeira tentativa.
    //
    // Antes gravava calada, e o resultado que dependia do campo vazio
    // simplesmente não saía: foi assim que as 76 coletas de 2026
    // ficaram sem o diâmetro do punho e sem o fracionamento inteiro, e
    // só se descobriu meses depois. O protocolo agora é fechado, e a
    // tela diz o que falta antes de deixar passar.
    //
    // A segunda tentativa grava. Medida que não deu para fazer no dia,
    // por lesão ou por falta de instrumento, continua sendo um caso
    // real, e travar de vez transformaria a coleta inteira em perda.
    if (pendencias.length && !insistindo) {
      setInsistindo(true);
      return onErro(
        `Faltam ${pendencias.length} medida(s) para a coleta ficar completa: `
        + `${pendencias.join(", ")}. Preencha o que faltar e grave novamente, ou confirme `
        + "a gravação para registrar a coleta incompleta.",
      );
    }
    setSalvando(true);
    try {
      await salvarAvaliacaoFisica({
        nucleo,
        atletaUid: atleta.uid,
        atletaNome: atleta.nome,
        modalidade: atleta.modalidade || "",
        data: form.data,
        origem: "propria",
        // Vão nulos quando são automáticos. A coleta guarda a escolha de
        // quem mediu, não o resultado dela: a idade e o pico continuam
        // resolvendo na leitura, e é isso que mantém a série
        // recalculável quando uma equação muda.
        protocoloGordura: form.protocoloGordura || null,
        estagioMaturacional: form.estagioMaturacional || null,
        peso: numeroOuNulo(form.peso),
        estatura: numeroOuNulo(form.estatura),
        estaturaSentado: numeroOuNulo(form.alturas.estaturaSentado),
        envergadura: numeroOuNulo(form.alturas.envergadura),
        alturaTrocanterica: numeroOuNulo(form.alturas.alturaTrocanterica),
        dobras: limpar(form.dobras),
        perimetros: limpar(form.perimetros),
        diametros: limpar(form.diametros),
      });
      await onSalvou();
    } catch (e) {
      onErro(`Não consegui gravar: ${e.message}`);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card titulo="Coleta">
      <div style={{ display: "grid", gap: ESP.lg }}>
        <div style={{
          display: "grid", gap: 10,
          gridTemplateColumns: isMobile ? "1fr" : "190px minmax(220px, 1fr) minmax(190px, 1fr)",
        }}>
          <Input label="Data da avaliação" type="date" value={form.data}
            onChange={v => mudar(null, "data", v)} />
          {/* O padrão é a idade decidir, e continua sendo: a primeira
              opção é o automático. O seletor existe para a escolha que é
              de verdade, dos 18 em diante Pollock ou Guedes, e para
              deixar o avaliador ver na tela qual equação vai rodar. */}
          <Sel
            label="Protocolo de gordura"
            value={form.protocoloGordura}
            onChange={v => mudar(null, "protocoloGordura", v)}
            options={[
              {
                value: "",
                label: automatico
                  ? `Automático pela idade: ${rotuloDoProtocolo(automatico)}`
                  : "Automático pela idade",
              },
              ...PROTOCOLOS_DE_GORDURA.map(x => ({ value: x, label: rotuloDoProtocolo(x) })),
            ]}
          />
          {/* O estágio maturacional escolhe a equação DENTRO de
              Deurenberg, e a diferença entre púbere e pós-púbere no mesmo
              somatório passa de um ponto de gordura. O Physical Test
              pergunta isso ao avaliador, e aqui é igual, com a sugestão
              vinda do pico de velocidade de crescimento. */}
          <Sel
            label="Estágio maturacional"
            value={form.estagioMaturacional}
            onChange={v => mudar(null, "estagioMaturacional", v)}
            options={[
              {
                value: "",
                label: estagioAutomatico
                  ? `Sugerido: ${rotuloDoEstagio(estagioAutomatico)}`
                  : "Sugerido pela idade",
              },
              ...ESTAGIOS_MATURACIONAIS.map(e => ({ value: e.id, label: e.rotulo })),
            ]}
          />
        </div>

        {/* O protocolo que está valendo, dito por extenso.
            Sem isso quem mede não tem como saber por que o peitoral
            apareceu num atleta e não no outro. */}
        {adequacao && adequacao.adequado === true && !adequacao.ressalva && (
          <div style={{
            ...TIPO.apoio, color: CINZA_ESC, background: FUNDO_SUAVE,
            border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "9px 12px",
          }}>
            {"Vai pelo protocolo de "}
            <strong style={{ color: TEXTO }}>{rotuloDoProtocolo(protocolo)}</strong>
            {idadeNaColeta != null && ` · ${dec(idadeNaColeta)} anos na data da coleta`}
            {protocolo === "deurenberg" && estagio
              ? ` · equação de ${rotuloDoEstagio(estagio).toLowerCase()}`
              : ""}
            {mat && mat.completo && !form.estagioMaturacional
              ? `, estágio sugerido pelo pico de crescimento (${mat.estagio})`
              : ""}
            {". As dobras pedidas são as dele, mais as do somatotipo."}
          </div>
        )}

        {/* Escolha que não serve para a idade do atleta.
            O aviso diz o motivo e diz o que o sistema vai fazer, porque as
            duas coisas interessam: a conta não sai pelo protocolo recusado,
            e quem lançar precisa saber disso antes de gravar. */}
        {adequacao && adequacao.adequado === false && (
          <StatusBox tipo="aviso" msg={
            `${rotuloDoProtocolo(protocolo)} não é adequado para este atleta. ${adequacao.motivo}`
            + (automatico && protocolo !== automatico
              ? ` O percentual de gordura vai sair por ${rotuloDoProtocolo(automatico)}, `
                + "que é o protocolo da idade, e a escolha fica gravada sem efeito."
              : "")
          } />
        )}

        {/* Cadastro incompleto: a pergunta não tem resposta, e o aviso
            cobra o campo em vez de culpar a escolha. */}
        {adequacao && adequacao.adequado === null && (
          <StatusBox tipo="aviso" msg={adequacao.motivo} />
        )}

        {adequacao && adequacao.ressalva && (
          <StatusBox tipo="aviso" msg={adequacao.ressalva} />
        )}

        {mistura && (
          <StatusBox tipo="aviso" msg={
            `As outras avaliações deste atleta saíram por ${anteriores.map(rotuloDoProtocolo).join(" e ")}, `
            + `e esta vai por ${rotuloDoProtocolo(protocolo)}. As equações medem dobras diferentes, `
            + "então a diferença entre as duas apareceria no gráfico como mudança do atleta. "
            + "Trocar de protocolo no meio da série só faz sentido se a série anterior for refeita."
          } />
        )}

        {GRUPOS.map(g => {
          const exigidos = exigidosDoGrupo(g.id);
          return (
            <div key={g.id}>
              <h4 style={{ margin: "0 0 8px", ...TIPO.corpo, color: TEXTO }}>
                {g.titulo}
                {g.unidade && (
                  <span style={{ fontWeight: 400, color: CINZA_ESC }}> (em {g.unidade})</span>
                )}
              </h4>
              <div style={{
                display: "grid", gap: 10,
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fill, minmax(150px, 1fr))",
              }}>
                {(g.campos || g.itens).map(c => {
                  const grupo = g.campos ? null : g.id;
                  const valor = grupo ? form[grupo][c.id] ?? "" : form[c.id] ?? "";
                  // O campo que algum resultado exige fica marcado
                  // enquanto está vazio. É o lembrete no lugar certo: na
                  // hora de medir, e não depois, quando o atleta já foi
                  // embora.
                  const exigida = exigidos.includes(c.id);
                  const pendente = exigida && !String(valor).trim();
                  return (
                    <div key={c.id}>
                      <Input
                        label={`${c.rotulo}${exigida ? " *" : ""}`}
                        type="number" value={valor}
                        onChange={v => mudar(grupo, c.id, v)}
                      />
                      {pendente && (
                        <div style={{ fontSize: 11, color: ATENCAO, marginTop: 2 }}>
                          {g.id === "dobras" ? "pedida pelo protocolo" : "pedida pelos resultados"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* O que falta, enquanto se mede. O mesmo texto que a gravação
            cobra, para a falta aparecer com o atleta ainda na frente. */}
        {pendencias.length > 0 && (
          <StatusBox tipo={insistindo ? "erro" : "aviso"} msg={
            `Faltam ${pendencias.length} medida(s) para a coleta ficar completa: `
            + `${pendencias.join(", ")}.`
            + (insistindo
              ? " Gravar novamente registra a coleta incompleta, e o que depende destes"
                + " campos não é calculado."
              : "")
          } />
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={salvar} disabled={salvando}>
            {salvando ? "Gravando..."
              : insistindo && pendencias.length
                ? `Gravar incompleta (faltam ${pendencias.length})`
                : "Gravar avaliação"}
          </Btn>
          <Btn variant="secondary" onClick={onCancelar} disabled={salvando}>Cancelar</Btn>
        </div>
      </div>
    </Card>
  );
}

// ── Medidas coletadas ────────────────────────────────────────

// A tabela das medidas cruas, um sítio por linha e uma coleta por
// coluna.
//
// É o formato do relatório de dobras e diâmetros do Physical Test, e é
// assim porque a pergunta que se faz aqui é vertical: a dobra do tríceps
// deste atleta caiu de março para agosto? Uma coleta por linha
// responderia a outra pergunta, e obrigaria a ler na diagonal.
//
// Sítio que ninguém mediu em coleta nenhuma não vira linha. A bateria
// tem mais de trinta campos e a maioria fica vazia em quase todo atleta;
// imprimir trinta linhas de travessão esconde as dez que têm medida.
const GRUPOS_DE_MEDIDA = [
  { id: "dobras", titulo: "Dobras cutâneas", unidade: "mm", itens: DOBRAS },
  { id: "perimetros", titulo: "Perímetros", unidade: "cm", itens: PERIMETROS_POR_CAMPO },
  { id: "diametros", titulo: "Diâmetros ósseos", unidade: "cm", itens: DIAMETROS },
];

// Peso, estatura e as alturas ficam no topo da coluna: são medidas de
// campo próprio no documento, e não de um dos três grupos.
const MEDIDAS_DE_TOPO = [
  { id: "peso", rotulo: "Peso", unidade: "kg" },
  { id: "estatura", rotulo: "Estatura", unidade: "cm" },
  ...ALTURAS.map(a => ({ id: a.id, rotulo: a.rotulo, unidade: "cm" })),
];

function TabelaDeMedidas({ coletas = [] }) {
  const ordenadas = [...coletas]
    .filter(c => c && c.data)
    .sort((a, b) => String(a.data).localeCompare(String(b.data)));

  if (!ordenadas.length) return <Vazio titulo="Nenhuma medida lançada." />;

  const valorDe = (c, grupo, id) => (grupo ? (c[grupo] || {})[id] : c[id]);
  const temAlguma = (grupo, id) =>
    ordenadas.some(c => numeroOuNulo(valorDe(c, grupo, id)) !== null);

  const blocos = [
    {
      titulo: "Peso, estatura e comprimentos",
      grupo: null,
      linhas: MEDIDAS_DE_TOPO.filter(m => temAlguma(null, m.id)),
    },
    ...GRUPOS_DE_MEDIDA.map(g => ({
      titulo: `${g.titulo} (em ${g.unidade})`,
      grupo: g.id,
      linhas: g.itens.filter(i => temAlguma(g.id, i.id)),
    })),
  ].filter(b => b.linhas.length > 0);

  // A coluna de diferença entre duas coletas vizinhas.
  //
  // Vai com sinal, e não em módulo: dobra que caiu e dobra que subiu são
  // leituras opostas, e o módulo apagaria justamente isso. Sem cor, de
  // propósito: subir é bom no perímetro do braço e ruim na dobra do
  // abdome, e pintar de verde ou vermelho julgaria pelo avaliador.
  function Diferenca({ de, para }) {
    if (de === null || para === null) return <span style={{ color: CINZA_ESC }}>—</span>;
    const d = Math.round((para - de) * 100) / 100;
    if (d === 0) return <span style={{ color: CINZA_ESC }}>=</span>;
    return (
      <span style={{ color: CINZA_ESC }}>
        {d > 0 ? "+" : "−"}{String(Math.abs(d)).replace(".", ",")}
      </span>
    );
  }

  // Cabeçalho e corpo intercalam data e diferença: 31/03, dif., 28/08. A
  // diferença fica ENTRE as duas colunas que ela compara, que é onde o
  // olho a procura.
  const colunas = ordenadas.length * 2 - 1 + 1;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", ...TIPO.apoio }}>
        <thead>
          <tr style={{ background: FUNDO_SUAVE }}>
            <Th>Medida</Th>
            {ordenadas.map((c, i) => (
              <Fragment key={c.data}>
                {i > 0 && <Th>dif.</Th>}
                <Th>{emBR(c.data)}</Th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {blocos.map(b => (
            <Fragment key={b.titulo}>
              <tr>
                <td colSpan={colunas} style={{
                  padding: "10px 10px 4px", ...TIPO.rotulo, color: CINZA_ESC,
                  borderTop: `1px solid ${BORDA}`,
                }}>{b.titulo}</td>
              </tr>
              {b.linhas.map(linha => {
                const valores = ordenadas.map(c => numeroOuNulo(valorDe(c, b.grupo, linha.id)));
                return (
                  <tr key={linha.id} style={{ borderTop: `1px solid ${BORDA}` }}>
                    <Td>{linha.rotulo}{linha.unidade && b.grupo === null
                      ? <span style={{ color: CINZA_ESC }}> ({linha.unidade})</span>
                      : null}</Td>
                    {valores.map((v, i) => (
                      <Fragment key={ordenadas[i].data}>
                        {i > 0 && (
                          <Td><Diferenca de={valores[i - 1]} para={v} /></Td>
                        )}
                        <Td forte={v !== null}>
                          {v === null
                            ? <span style={{ color: CINZA_ESC }}>—</span>
                            : String(v).replace(".", ",")}
                        </Td>
                      </Fragment>
                    ))}
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Síntese da equipe ────────────────────────────────────────
//
// O bloco que transforma número em orientação de trabalho.
//
// A avaliação inteira acima é descrição: quanto o atleta mede, o que
// mudou desde a última vez, onde ele está na carta. Nada disso diz o que
// a equipe vai fazer com isso, e é essa a ponte que faltava. As
// recomendações do COI para acompanhamento de jovens atletas pedem
// justamente essa ligação entre avaliar, interpretar e aplicar ao
// treino, e sem ela a avaliação vira arquivo.
//
// ── Por que escrita por pessoa, e não gerada ────────────────
//
// Porque é julgamento, não conta. O sistema sabe que a gordura caiu 1,5
// ponto e que o atleta está em Circa-PVC; só o técnico e o preparador
// sabem se isso é resposta ao bloco de força, efeito do estirão ou
// consequência de um mês de férias. Texto gerado aqui soaria autoridade
// que o sistema não tem.
//
// ── Onde ela fica gravada ───────────────────────────────────
//
// Dentro do documento da própria coleta, em `sintese`. É a leitura
// daquela avaliação, feita naquela data, e guardá-la à parte faria a
// síntese de março sobreviver sozinha depois que a de agosto chegasse. A
// cada nova coleta, uma nova síntese, e a anterior continua onde estava.
const CAMPOS_DA_SINTESE = [
  {
    id: "avancos",
    rotulo: "Principais avanços",
    ajuda: "O que melhorou desde a última avaliação e merece ser mantido no treino.",
  },
  {
    id: "atencao",
    rotulo: "Pontos de atenção",
    ajuda: "O que piorou, o que não evoluiu como esperado e o que precisa de correção.",
  },
  {
    id: "prioridades",
    rotulo: "Prioridades do próximo período",
    ajuda: "Em que o treino vai focar até a próxima avaliação.",
  },
];

function textoVazio(s) {
  return !String(s || "").trim();
}

function SinteseDaEquipe({ ponto, coleta, atleta, nucleo, podeEditar, onSalvou, onErro }) {
  const gravada = (coleta && coleta.sintese) || null;
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState(() => ({
    avancos: "", atencao: "", prioridades: "", responsavel: "", proximaAvaliacao: "",
  }));

  // Trocar de coleta fecha a edição e recarrega o que estava gravado:
  // sem isto, o texto digitado para março seguiria na tela de agosto.
  useEffect(() => {
    setEditando(false);
    setForm({
      avancos: (gravada && gravada.avancos) || "",
      atencao: (gravada && gravada.atencao) || "",
      prioridades: (gravada && gravada.prioridades) || "",
      responsavel: (gravada && gravada.responsavel) || "",
      proximaAvaliacao: (gravada && gravada.proximaAvaliacao) || "",
    });
  }, [ponto.data, gravada]);

  const vazia = !gravada || (
    CAMPOS_DA_SINTESE.every(c => textoVazio(gravada[c.id]))
    && textoVazio(gravada.responsavel) && textoVazio(gravada.proximaAvaliacao)
  );

  async function salvar() {
    setSalvando(true);
    try {
      await salvarAvaliacaoFisica({
        nucleo,
        atletaUid: atleta.uid,
        atletaNome: atleta.nome,
        data: ponto.data,
        sintese: {
          avancos: form.avancos.trim(),
          atencao: form.atencao.trim(),
          prioridades: form.prioridades.trim(),
          responsavel: form.responsavel.trim(),
          proximaAvaliacao: form.proximaAvaliacao || "",
          escritaEm: new Date().toISOString().slice(0, 10),
        },
      });
      setEditando(false);
      await onSalvou();
    } catch (e) {
      onErro(`Não consegui gravar a síntese: ${e.message}`);
    } finally {
      setSalvando(false);
    }
  }

  if (!editando) {
    return (
      <div style={{ display: "grid", gap: ESP.md }}>
        {vazia ? (
          <Vazio titulo={podeEditar
            ? "Sem síntese desta avaliação. É o bloco que registra o encaminhamento da equipe a partir destes números."
            : "A equipe ainda não escreveu a síntese desta avaliação."} />
        ) : (
          <>
            {CAMPOS_DA_SINTESE.map(c => (
              textoVazio(gravada[c.id]) ? null : (
                <div key={c.id}>
                  <div style={{ ...TIPO.rotulo, color: CINZA_ESC }}>{c.rotulo}</div>
                  <div style={{ ...TIPO.corpo, color: TEXTO, whiteSpace: "pre-wrap" }}>
                    {gravada[c.id]}
                  </div>
                </div>
              )
            ))}
            <div style={{ display: "flex", gap: ESP.xl, flexWrap: "wrap", ...TIPO.apoio }}>
              {!textoVazio(gravada.responsavel) && (
                <span><span style={{ color: CINZA_ESC }}>Responsável: </span>
                  <strong style={{ color: TEXTO }}>{gravada.responsavel}</strong></span>
              )}
              {!textoVazio(gravada.proximaAvaliacao) && (
                <span><span style={{ color: CINZA_ESC }}>Próxima avaliação: </span>
                  <strong style={{ color: TEXTO }}>{emBR(gravada.proximaAvaliacao)}</strong></span>
              )}
              {!textoVazio(gravada.escritaEm) && (
                <span style={{ color: CINZA_ESC }}>escrita em {emBR(gravada.escritaEm)}</span>
              )}
            </div>
          </>
        )}
        {podeEditar && (
          <div>
            <Btn variant="secondary" tamanho="sm" onClick={() => setEditando(true)}>
              {vazia ? "Escrever síntese" : "Editar síntese"}
            </Btn>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: ESP.md }}>
      {CAMPOS_DA_SINTESE.map(c => (
        <div key={c.id}>
          <Rotulo>{c.rotulo}</Rotulo>
          <textarea
            value={form[c.id]}
            onChange={e => setForm(f => ({ ...f, [c.id]: e.target.value }))}
            rows={3}
            style={{ ...ESTILO_CAMPO, width: "100%", resize: "vertical", lineHeight: 1.5 }}
          />
          <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{c.ajuda}</div>
        </div>
      ))}
      <div style={{
        display: "grid", gap: ESP.md,
        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
      }}>
        <Input label="Responsável" value={form.responsavel}
          onChange={v => setForm(f => ({ ...f, responsavel: v }))}
          placeholder="quem assina esta leitura" />
        <Input label="Próxima avaliação" type="date" value={form.proximaAvaliacao}
          onChange={v => setForm(f => ({ ...f, proximaAvaliacao: v }))} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn onClick={salvar} disabled={salvando}>
          {salvando ? "Gravando..." : "Gravar síntese"}
        </Btn>
        <Btn variant="secondary" onClick={() => setEditando(false)} disabled={salvando}>
          Cancelar
        </Btn>
      </div>
    </div>
  );
}

// ── Detalhe de uma coleta ────────────────────────────────────

function DetalheDaColeta({ ponto, isMobile }) {
  const f = ponto.fracionamento;
  const ind = ponto.indices || {};
  const comp = ponto.deurenberg || null;

  return (
    <div style={{ display: "grid", gap: ESP.xl }}>
      <div>
        {/* A mesma divisão do relatório, e na mesma ordem: primeiro a
            medida e a composição, depois o fracionamento.

            Até 29/10/2026 a tela punha a massa gorda de Deurenberg sob o
            rótulo "Massa adiposa" quando o fracionamento não fechava, e
            sem a unidade. São métodos diferentes: a adiposa é do
            fracionamento, a gorda é peso vezes o percentual das dobras.
            Mostrá-las com o mesmo nome fazia o número mudar de origem
            sem que nada avisasse. */}
        <Subtitulo>Resultados da coleta</Subtitulo>
        <FileiraDeEstatisticas>
          <Estatistica rotulo="Idade na coleta"
            valor={ponto.idade != null ? `${dec(ponto.idade)} anos` : "—"} />
          <Estatistica rotulo="Peso" valor={`${dec(ponto.peso, 1)} kg`} />
          <Estatistica rotulo="Estatura" valor={`${dec(ponto.estatura, 1)} cm`} />
          <Estatistica rotulo="IMC" valor={dec(ind.imc)} />
          <Estatistica rotulo="Gordura"
            valor={comp && comp.completo ? `${dec(comp.percentualGordura)}%` : "—"}
            contexto={comp && comp.completo ? comp.classificacao : null} />
          <Estatistica rotulo="Massa gorda"
            valor={comp && comp.completo ? `${dec(comp.massaGorda, 1)} kg` : "—"}
            contexto="peso vezes o percentual" />
          <Estatistica rotulo="Massa magra"
            valor={comp && comp.completo ? `${dec(comp.massaMagra, 1)} kg` : "—"} />
          <Estatistica rotulo="Protocolo"
            valor={comp && comp.protocolo ? (rotuloDoProtocolo(comp.protocolo) || "—") : "—"} />
        </FileiraDeEstatisticas>
      </div>

      {/* A maturação ficava só na coluna da tabela da série, enquanto o
          relatório trazia o bloco inteiro, com o método e o laudo. Quem
          lia a tela não via de onde vinha o estágio. */}
      {(() => {
        const laudoMat = laudoDaMaturacao(ponto.maturacao);
        if (!laudoMat) return null;
        return (
          <div>
            <Subtitulo>Maturação</Subtitulo>
            <FileiraDeEstatisticas>
              <Estatistica rotulo="Estágio" valor={laudoMat.estagio} />
              <Estatistica rotulo="Pico estimado"
                valor={`${dec(ponto.maturacao.idadeNoPico)} anos`}
                contexto={`± ${dec(ponto.maturacao.erroPadrao, 2)} ano`} />
              <Estatistica rotulo="Método"
                valor={ponto.maturacao.metodo === "mirwald" ? "Mirwald" : "Moore"} />
              <Estatistica rotulo="Estágio de Deurenberg"
                valor={rotuloDoEstagio(ponto.estagioMaturacional) || "não informado"} />
            </FileiraDeEstatisticas>
            <div style={{ ...TIPO.apoio, color: TEXTO, marginTop: 6, lineHeight: 1.6 }}>
              {laudoMat.texto}
            </div>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 4, lineHeight: 1.6 }}>
              {laudoMat.ressalva}
            </div>
          </div>
        );
      })()}

      <div>
        <Subtitulo>Fracionamento da massa corporal</Subtitulo>
        <FileiraDeEstatisticas>
          <Estatistica rotulo="Massa adiposa"
            valor={f && f.completo ? `${dec(f.massaGorda, 2)} kg` : "—"}
            contexto={f && f.completo ? `${dec(f.percentualGordura)}%` : null} />
          <Estatistica rotulo="Massa muscular"
            valor={f && f.completo ? `${dec(f.massaMuscular, 2)} kg` : "—"}
            contexto={f && f.completo ? `${dec(f.percentualMuscular)}%` : null} />
          <Estatistica rotulo="Massa óssea"
            valor={f && f.completo ? `${dec(f.massaOssea, 2)} kg` : "—"}
            contexto={f && f.completo ? `${dec(f.percentualOsseo)}%` : null} />
          <Estatistica rotulo="Massa residual"
            valor={f && f.completo ? `${dec(f.massaResidual, 2)} kg` : "—"}
            contexto={f && f.completo ? `${dec(f.percentualResidual)}%` : null} />
        </FileiraDeEstatisticas>
      </div>

      {/* O que falta para o fracionamento, dito por nome. É o estado de
          todas as coletas de 2026: sem o diâmetro do punho não sai peso
          ósseo, e sem peso ósseo não sai peso muscular, porque ele é
          subtração. */}
      {f && !f.completo && f.faltam.length > 0 && (
        <StatusBox tipo="aviso" msg={
          "As quatro massas não são calculadas nesta coleta. Falta medir: "
          + `${f.faltam.map(rotuloDoCampo).join(", ")}. `
          + "O percentual de gordura e o somatotipo não dependem disso e continuam valendo."
        } />
      )}
      {f && f.massaMuscularNegativa && (
        <StatusBox tipo="erro" msg={
          "A soma de gordura, osso e massa residual superou o peso corporal, o que é "
          + "impossível: há erro de medida em alguma das três. Confira as dobras e o peso "
          + "antes de usar este resultado."
        } />
      )}

      {f && f.completo && (
        <div>
          <Subtitulo>Composição corporal</Subtitulo>
          <GraficoDeComposicao fracionamento={f} isMobile={isMobile} />
          {/* Os dois avisos que ficavam aqui saíram em 15/10/2026, a
              pedido do Diego: a ressalva da faixa de idade da equação de
              Rocha e a marca de punho reconstruído.

              A reconstrução foi de uma vez só, para recuperar as coletas
              de 2026 que não tinham o diâmetro do punho, e daqui em
              diante o protocolo é fechado: a coleta não grava sem a
              bateria completa. As duas explicações continuam em "De onde
              vêm estas classificações", que é onde a procedência dos
              números mora, e a marca `biestiloideReconstruido` continua
              gravada na coleta. */}
        </div>
      )}

      <div>
        <Subtitulo>Índices de saúde</Subtitulo>
        <FileiraDeEstatisticas>
          <Estatistica rotulo="Somatório das dobras"
            valor={ind.somaDasDobras != null ? `${dec(ind.somaDasDobras)} mm` : "—"}
          />
          <Estatistica rotulo="Cintura / quadril" valor={dec(ind.razaoCinturaQuadril, 2)}
            contexto={ind.classificacaoRCQ} />
          <Estatistica rotulo="Cintura / estatura" valor={dec(ind.razaoCinturaEstatura, 2)}
            contexto={ind.cinturaAcimaDaMetadeDaEstatura === true ? "acima de 0,50" : null} />
          <Estatistica rotulo="Índice de conicidade" valor={dec(ind.indiceDeConicidade, 2)} />
        </FileiraDeEstatisticas>
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 6 }}>
          O somatório das dobras é medida direta. O percentual de gordura é estimativa
          calculada a partir dele.
        </div>

        {/* Proporções. Só aparecem quando a medida existe, e as duas
            dependem de campos que a coleta de 2026 não tinha: estatura
            sentado e envergadura. */}
        {(ind.indiceCormico != null || ind.razaoEnvergaduraEstatura != null) && (
          <div style={{ marginTop: ESP.lg }}>
            <Subtitulo>Proporções</Subtitulo>
            <FileiraDeEstatisticas>
              {ind.indiceCormico != null && (
                <Estatistica rotulo="Índice córmico" valor={`${dec(ind.indiceCormico)}%`}
                  contexto="quanto da estatura é tronco" />
              )}
              {ind.razaoEnvergaduraEstatura != null && (
                <Estatistica rotulo="Envergadura / estatura"
                  valor={dec(ind.razaoEnvergaduraEstatura, 3)}
                  contexto="1,00 é braço do tamanho da altura" />
              )}
            </FileiraDeEstatisticas>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 6 }}>
              As duas mudam no estirão, porque a perna e o braço crescem antes do tronco.
              São razões entre medidas do próprio atleta, sem equação de população no
              meio, e servem para acompanhar a mudança de proporção dele.
            </div>
          </div>
        )}
        {/* O aviso sobre pontos de corte de adulto saiu em 15/10/2026, a
            pedido do Diego: abaixo dos 18 anos o sistema não classifica
            nada mesmo, então o texto explicava uma classificação que não
            aparece. O valor continua, para acompanhar o próprio atleta. */}
      </div>
    </div>
  );
}

// ── Apoio ────────────────────────────────────────────────────

// Rótulo de cada gráfico. Pequeno de propósito: o título do cartão já
// diz "Evolução", e estes três só dizem de quê.
function Subtitulo({ children }) {
  return (
    <div style={{
      ...TIPO.rotulo, color: CINZA_ESC, marginBottom: 6,
    }}>{children}</div>
  );
}

function Th({ children }) {
  return (
    <th style={{
      textAlign: "left", padding: "8px 10px", ...TIPO.apoio,
      color: CINZA_ESC, fontWeight: 600, whiteSpace: "nowrap",
    }}>{children}</th>
  );
}

function Td({ children, forte }) {
  return (
    <td style={{
      padding: "8px 10px", color: TEXTO, whiteSpace: "nowrap",
      fontWeight: forte ? 600 : 400,
    }}>{children}</td>
  );
}

function emBR(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

// Número com vírgula, que é como se escreve em português. `toFixed`
// devolve ponto, e "15.8%" ao lado de "66,2%" na mesma tela é descuido
// visível. A ficha do atleta já imprime assim, e as duas telas mostram a
// mesma série.
function dec(v, casas = 1) {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(casas).replace(".", ",");
}

// O número como foi medido, sem forçar casa decimal: peso de 41,2 não
// vira 41,20 e estatura de 148 não vira 148,0.
function num(v, unidade = "") {
  if (v == null || !Number.isFinite(v)) return "—";
  const texto = String(v).replace(".", ",");
  return unidade ? `${texto} ${unidade}` : texto;
}

function numeroOuNulo(v) {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Tira do objeto o que ficou vazio, para não gravar campo nulo.
function limpar(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    const n = numeroOuNulo(v);
    if (n !== null) out[k] = n;
  });
  return out;
}

// "dobras.supraEspinhal" vira "Supraespinhal".
//
// O módulo devolve o caminho do campo, que é o que a tela precisa para
// saber onde ele fica. Quem lê precisa do nome do sítio.
const ROTULOS = Object.fromEntries([
  ...DOBRAS.map(d => [`dobras.${d.id}`, d.rotulo]),
  ...PERIMETROS_POR_CAMPO.map(p => [`perimetros.${p.id}`, p.rotulo]),
  ...DIAMETROS.map(d => [`diametros.${d.id}`, d.rotulo]),
  ...ALTURAS.map(a => [a.id, a.rotulo]),
  ...DOBRAS.map(d => [d.id, d.rotulo]),
  ["estatura", "estatura"],
  ["peso", "peso"],
  // Estes dois não são medida: entram na lista do que falta para o
  // fracionamento e precisam de nome de gente.
  ["percentualGordura", "o percentual de gordura"],
  ["sexo", "o sexo no cadastro"],
]);

export function rotuloDoCampo(caminho) {
  return ROTULOS[caminho] || String(caminho).split(".").pop();
}
